import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { quizAPI } from '../services/api';
import { FiArrowLeft, FiClock, FiCheckCircle, FiXCircle, FiTrendingUp, FiAward, FiTarget, FiFilter, FiChevronDown, FiMenu, FiX, FiSettings, FiLogOut } from 'react-icons/fi';
import DOMPurify from 'dompurify';
const QuizHistory = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [filter, setFilter] = useState('all'); // all, exam, practice
  const [sortBy, setSortBy] = useState('recent'); // recent, score
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await quizAPI.getQuizHistory(user.id);
      if (response.data.success) {
        setHistory(response.data.history);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  };

  const getGradeColor = (percentage) => {
    if (percentage >= 90) return 'text-green-600 bg-green-50';
    if (percentage >= 70) return 'text-blue-600 bg-blue-50';
    if (percentage >= 50) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getGrade = (percentage) => {
    if (percentage >= 90) return 'A+';
    if (percentage >= 80) return 'A';
    if (percentage >= 70) return 'B';
    if (percentage >= 60) return 'C';
    if (percentage >= 50) return 'D';
    return 'F';
  };

  // Filter and sort history
  const filteredHistory = history
    .filter(quiz => {
      if (filter === 'all') return true;
      if (filter === 'exam') return quiz.type === 'exam';
      if (filter === 'practice') return quiz.type !== 'exam';
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'recent') {
        return new Date(b.created_at) - new Date(a.created_at);
      }
      if (sortBy === 'score') {
        return b.percentage - a.percentage;
      }
      return 0;
    });

  // Calculate stats
  const totalQuizzes = history.length;
  const avgScore = history.length > 0
    ? Math.round(history.reduce((sum, q) => sum + (Number(q.percentage) || 0), 0) / history.length)
    : 0;
  const bestScore = history.length > 0
    ? Math.max(...history.map(q => Number(q.percentage) || 0))
    : 0;

  return (
    <>
      {/* MOBILE VIEW - App Style */}
      <div className="md:hidden min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pb-24">
        {/* Hero Header */}
        <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-b-2xl shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full -mr-24 -mt-24"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full -ml-16 -mb-16"></div>
          
          <div className="relative px-4 pt-5 pb-8">
            <div className="flex items-center justify-between mb-4">
              <Link to="/dashboard" className="inline-flex items-center text-white/90 hover:text-white active:scale-95 transition-transform">
                <FiArrowLeft className="w-5 h-5 mr-2" />
                <span className="text-sm font-medium">Back</span>
              </Link>
              
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center text-white font-bold text-base shadow-lg">
                  {user?.firstname?.[0]}{user?.lastname?.[0] || user?.firstname?.[1]}
                </div>
                <button
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                  className="w-9 h-9 bg-white/20 backdrop-blur-lg rounded-full flex items-center justify-center hover:bg-white/30 transition-all active:scale-95"
                >
                  {showMobileMenu ? (
                    <FiX className="w-5 h-5 text-white" />
                  ) : (
                    <FiMenu className="w-5 h-5 text-white" />
                  )}
                </button>
              </div>
            </div>
            
            <h1 className="text-white text-2xl font-bold mb-1">Quiz History</h1>
            <p className="text-white/90 text-sm">{totalQuizzes} test{totalQuizzes !== 1 ? 's' : ''} completed</p>
          </div>

        </div>

        {/* Stats Summary - Overlapping */}
        <div className="px-4 mt-4 mb-6">
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-white rounded-xl shadow-lg p-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mb-2">
                <FiTarget className="w-5 h-5 text-purple-600" />
              </div>
              <p className="text-xl font-bold text-gray-800">{totalQuizzes}</p>
              <p className="text-[10px] text-gray-500 font-medium">Total</p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-2">
                <FiTrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <p className="text-xl font-bold text-gray-800">{avgScore}%</p>
              <p className="text-[10px] text-gray-500 font-medium">Average</p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-2">
                <FiAward className="w-5 h-5 text-green-600" />
              </div>
              <p className="text-xl font-bold text-gray-800">{bestScore}%</p>
              <p className="text-[10px] text-gray-500 font-medium">Best</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="px-4 mb-4">
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all ${
                filter === 'all'
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              All ({history.length})
            </button>
            <button
              onClick={() => setFilter('exam')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all ${
                filter === 'exam'
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              Tests ({history.filter(h => h.type === 'exam').length})
            </button>
            <button
              onClick={() => setFilter('practice')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all ${
                filter === 'practice'
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              Practice ({history.filter(h => h.type !== 'exam').length})
            </button>
          </div>
        </div>

        {/* History List */}
        <div className="px-4 space-y-3">
          {loading ? (
            // Mobile Loading Skeleton
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
                <div className="flex items-center justify-between mb-3">
                  <div className="h-6 bg-gray-200 rounded w-24"></div>
                  <div className="h-8 w-16 bg-gray-200 rounded"></div>
                </div>
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))
          ) : filteredHistory.length === 0 ? (
            // Empty State
            <div className="bg-white rounded-2xl shadow-md p-8 text-center mt-8">
              <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiClock className="w-10 h-10 text-purple-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">No History Yet</h3>
              <p className="text-gray-600 text-sm mb-6">Start taking tests to see your progress here</p>
              <Link 
                to="/my-courses" 
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all active:scale-95"
              >
                Browse Tests
              </Link>
            </div>
          ) : (
            filteredHistory.map((quiz, index) => {
              let quizData = null;
              if (quiz.quiz_data) {
                try {
                  quizData = typeof quiz.quiz_data === 'string'
                    ? JSON.parse(quiz.quiz_data)
                    : quiz.quiz_data;
                } catch {
                  quizData = null;
                }
              }
              const isExam = quiz.type === 'exam';
              const scoreColor = quiz.percentage >= 70 ? 'from-green-500 to-emerald-600' : 
                               quiz.percentage >= 50 ? 'from-yellow-500 to-orange-600' : 
                               'from-red-500 to-pink-600';
              
              return (
                <div key={index} className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-all p-4">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-3">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${
                      isExam 
                        ? 'bg-purple-100 text-purple-700' 
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {isExam ? '📝 Test' : '🎯 Practice'}
                    </span>
                    <div className={`px-4 py-2 rounded-lg bg-gradient-to-r ${scoreColor} text-white font-bold text-lg shadow-lg`}>
                      {quiz.percentage}%
                    </div>
                  </div>

                  {/* Title */}
                  {isExam && quiz.exam_name && (
                    <h3 className="font-bold text-gray-800 mb-2 text-sm">{quiz.exam_name}</h3>
                  )}

                  {/* Stats */}
                  <div className="flex items-center gap-4 mb-3">
                    <div className="flex items-center gap-1 text-gray-600">
                      <FiClock className="w-4 h-4" />
                      <span className="text-xs">
                        {new Date(quiz.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-green-600 font-bold">✓ {quiz.correct_answers}</span>
                      <span className="text-red-600 font-bold">✗ {quiz.total_questions - quiz.correct_answers}</span>
                      <span className="text-gray-500 font-medium">{quiz.total_questions} Q</span>
                    </div>
                  </div>

                  {/* Grade Badge */}
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-lg">
                    <FiAward className={`w-4 h-4 ${getGradeColor(quiz.percentage).split(' ')[0]}`} />
                    <span className={`text-sm font-bold ${getGradeColor(quiz.percentage).split(' ')[0]}`}>
                      Grade {getGrade(quiz.percentage)}
                    </span>
                  </div>

                  {/* View Details Button */}
                  {quizData && (
                    <button
                      onClick={() => setSelectedQuiz(selectedQuiz === index ? null : index)}
                      className="w-full mt-4 pt-3 border-t border-gray-100 text-purple-600 font-semibold text-sm flex items-center justify-center gap-1 hover:text-purple-700 active:scale-95 transition-all"
                    >
                      {selectedQuiz === index ? 'Hide' : 'View'} Details
                      <FiChevronDown className={`w-4 h-4 transition-transform ${selectedQuiz === index ? 'rotate-180' : ''}`} />
                    </button>
                  )}

                  {/* Detailed Results */}
                  {selectedQuiz === index && quizData && quizData.results && (
                    <div className="mt-4 pt-4 border-t border-gray-200 space-y-3 max-h-96 overflow-y-auto">
                      {quizData.results.map((result, idx) => (
                        <div
                          key={idx}
                          className={`p-3 rounded-lg border-2 ${
                            result.is_correct
                              ? 'border-green-200 bg-green-50'
                              : 'border-red-200 bg-red-50'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <p className="font-semibold text-gray-800 text-sm flex-1 pr-2">
                              Q{idx + 1}.{' '}
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: DOMPurify.sanitize(result.question || ''),
                                }}
                              />
                            </p>
                            {result.is_correct ? (
                              <FiCheckCircle className="text-green-500 w-5 h-5 flex-shrink-0" />
                            ) : (
                              <FiXCircle className="text-red-500 w-5 h-5 flex-shrink-0" />
                            )}
                          </div>
                          
                          <div className="text-xs space-y-1">
                            <p>
                              <span className="font-medium">Your Answer:</span>{' '}
                              <span 
                                className={result.is_correct ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}
                                dangerouslySetInnerHTML={{
                                  __html: DOMPurify.sanitize(result.selected || ''),
                                }}
                              />
                            </p>
                            {!result.is_correct && (
                              <p>
                                <span className="font-medium">Correct:</span>{' '}
                                <span 
                                  className="text-green-600 font-semibold"
                                  dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(result.correct || ''),
                                  }}
                                />
                              </p>
                            )}
                            {result.explanation && (
                              <div className="mt-2 pt-2 border-t border-gray-200">
                                <p className="font-medium text-gray-700">Explanation:</p>
                                <p 
                                  className="text-gray-600 mt-1"
                                  dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(result.explanation || ''),
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Mobile Menu Modal - Fixed Positioning */}
        {showMobileMenu && (
          <>
            {/* Overlay */}
            <div 
              className="fixed inset-0 bg-black/50 z-50"
              onClick={() => setShowMobileMenu(false)}
            ></div>
            
            {/* Dropdown Menu */}
            <div className="fixed top-16 right-4 w-72 bg-white rounded-2xl shadow-2xl z-50 overflow-hidden">
              <div className="p-4 bg-gradient-to-br from-blue-500 to-purple-600">
                <div className="flex items-center space-x-3 text-white">
                  <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-lg flex items-center justify-center font-bold text-lg">
                    {user?.firstname?.[0]}{user?.lastname?.[0] || user?.firstname?.[1]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold truncate">{user?.firstname} {user?.lastname}</p>
                    <p className="text-xs text-white/80 truncate">{user?.email}</p>
                  </div>
                </div>
              </div>
              
              <div className="p-2">
                <Link
                  to="/profile"
                  onClick={() => setShowMobileMenu(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 transition-colors active:scale-95"
                >
                  <FiSettings className="w-5 h-5 text-gray-600" />
                  <span className="font-medium text-gray-700">Profile Settings</span>
                </Link>
                
                <button
                  onClick={async () => {
                    setShowMobileMenu(false);
                    await logout();
                    navigate('/login');
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-red-50 transition-colors active:scale-95 text-red-600"
                >
                  <FiLogOut className="w-5 h-5" />
                  <span className="font-medium">Logout</span>
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* DESKTOP VIEW - Traditional */}
      <div className="hidden md:block min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Link to="/dashboard" className="mr-4 p-2 rounded-lg hover:bg-gray-100">
                  <FiArrowLeft className="w-5 h-5" />
                </Link>
                <h1 className="text-2xl font-bold text-gray-800">Quiz History</h1>
              </div>
              <div className="flex items-center space-x-2 text-primary-500">
                <FiTrendingUp className="w-5 h-5" />
                <span className="font-semibold">{history.length} Quizzes</span>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
          <div className="card">
            <div className="flex items-center">
              <div className="bg-purple-500 p-4 rounded-lg mr-4">
                <FiTarget className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-gray-500 text-sm">Total Tests</p>
                <p className="text-3xl font-bold text-purple-600">{totalQuizzes}</p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center">
              <div className="bg-blue-500 p-4 rounded-lg mr-4">
                <FiTrendingUp className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-gray-500 text-sm">Average Score</p>
                <p className="text-3xl font-bold text-blue-600">{avgScore}%</p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center">
              <div className="bg-green-500 p-4 rounded-lg mr-4">
                <FiAward className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-gray-500 text-sm">Best Score</p>
                <p className="text-3xl font-bold text-green-600">{bestScore}%</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                filter === 'all'
                  ? 'bg-primary-500 text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              All ({history.length})
            </button>
            <button
              onClick={() => setFilter('exam')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                filter === 'exam'
                  ? 'bg-primary-500 text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              Tests ({history.filter(h => h.type === 'exam').length})
            </button>
            <button
              onClick={() => setFilter('practice')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                filter === 'practice'
                  ? 'bg-primary-500 text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              Practice ({history.filter(h => h.type !== 'exam').length})
            </button>
          </div>
          
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <option value="recent">Most Recent</option>
            <option value="score">Highest Score</option>
          </select>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary-500 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading history...</p>
          </div>
        ) : filteredHistory.length === 0 ? (
          <div className="card text-center py-12">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiClock className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">No Quiz History</h3>
            <p className="text-gray-600 mb-6">You haven't taken any quizzes yet</p>
            <Link to="/dashboard" className="btn-primary inline-block">
              Take Your First Quiz
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {filteredHistory.map((quiz, index) => {
              let quizData = null;
              if (quiz.quiz_data) {
                try {
                  quizData = typeof quiz.quiz_data === 'string'
                    ? JSON.parse(quiz.quiz_data)
                    : quiz.quiz_data;
                } catch {
                  quizData = null;
                }
              }
              const isExam = quiz.type === 'exam';
              
              return (
                <div key={index} className="card hover:shadow-xl transition-all">
                  {/* Type Badge */}
                  <div className="flex items-center justify-between mb-3">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${
                      isExam 
                        ? 'bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 border border-purple-300' 
                        : 'bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-700 border border-blue-300'
                    }`}>
                      {isExam ? '📝 Test Series' : '🎯 Practice Quiz'}
                    </span>
                    {isExam && quiz?.exam_name && (
                      <span className="text-sm font-semibold text-gray-700">{quiz.exam_name}</span>
                    )}
                  </div>

                  <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                    {/* Quiz Info */}
                    <div className="flex-1 mb-4 md:mb-0">
                      <div className="flex items-center space-x-3 mb-2">
                        <FiClock className="text-gray-400" />
                        <span className="text-sm text-gray-500">
                          {new Date(quiz.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-4">
                        <div>
                          <p className="text-gray-600 text-sm">Questions</p>
                          <p className="text-2xl font-bold text-gray-800">{quiz.total_questions}</p>
                        </div>
                        <div>
                          <p className="text-gray-600 text-sm">Correct</p>
                          <p className="text-2xl font-bold text-green-600">{quiz.correct_answers}</p>
                        </div>
                        <div>
                          <p className="text-gray-600 text-sm">Wrong</p>
                          <p className="text-2xl font-bold text-red-600">
                            {quiz.total_questions - quiz.correct_answers}
                          </p>
                        </div>
                      </div>
                      
                      {/* Extra info for exams */}
                      {isExam && quizData && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <div className="flex items-center space-x-4 text-xs">
                            <span className="text-green-600 font-semibold">
                              +{quizData.total_marks} marks
                            </span>
                            <span className="text-red-600 font-semibold">
                              -{quizData.negative_marks} marks
                            </span>
                            <span className="text-indigo-600 font-bold">
                              Final: {quizData.actual_score}/{quizData.max_score}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Score Display */}
                    <div className="flex items-center space-x-4">
                      <div className={`px-6 py-4 rounded-lg ${getGradeColor(quiz.percentage)}`}>
                        <p className="text-sm font-medium">Grade</p>
                        <p className="text-4xl font-bold">{getGrade(quiz.percentage)}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-600 mb-1">Score</p>
                        <div className={`text-3xl font-bold ${
                          quiz.percentage >= 70 ? 'text-green-500' :
                          quiz.percentage >= 50 ? 'text-yellow-500' :
                          'text-red-500'
                        }`}>
                          {quiz.percentage}%
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* View Details Button */}
                  {quizData && (
                    <div className="mt-4 pt-4 border-t">
                      <button
                        onClick={() => setSelectedQuiz(selectedQuiz === index ? null : index)}
                        className="text-primary-500 hover:text-primary-600 font-medium text-sm"
                      >
                        {selectedQuiz === index ? 'Hide Details' : 'View Details'} →
                      </button>

                      {/* Detailed Results */}
                      {selectedQuiz === index && quizData.results && (
                        <div className="mt-4 space-y-3">
                          {quizData.results.map((result, idx) => (
                            <div
                              key={idx}
                              className={`p-4 rounded-lg border-2 ${
                                result.is_correct
                                  ? 'border-green-200 bg-green-50'
                                  : 'border-red-200 bg-red-50'
                              }`}
                            >
                              <div className="flex items-start justify-between mb-2">
                                <h4 className="font-semibold text-gray-800 flex-1">
                                  Q{idx + 1}.{' '}
                                  <span
                                    dangerouslySetInnerHTML={{
                                      __html: DOMPurify.sanitize(result.question || ''),
                                    }}
                                  />
                                </h4>
                                {result.is_correct ? (
                                  <FiCheckCircle className="text-green-500 w-6 h-6 flex-shrink-0 ml-2" />
                                ) : (
                                  <FiXCircle className="text-red-500 w-6 h-6 flex-shrink-0 ml-2" />
                                )}
                              </div>
                              
                              <div className="text-sm space-y-1">
                                <p>
                                  <span className="font-medium">Your Answer:</span>{' '}
                                  <span 
                                    className={result.is_correct ? 'text-green-600' : 'text-red-600'}
                                    dangerouslySetInnerHTML={{
                                      __html: DOMPurify.sanitize(result.selected || ''),
                                    }}
                                  />
                                </p>
                                {!result.is_correct && (
                                  <p>
                                    <span className="font-medium">Correct Answer:</span>{' '}
                                    <span 
                                      className="text-green-600"
                                      dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(result.correct || ''),
                                      }}
                                    />
                                  </p>
                                )}
                                {result.explanation && (
                                  <div className="mt-2 pt-2 border-t border-gray-200">
                                    <p className="font-medium text-gray-700">Explanation:</p>
                                    <p 
                                      className="text-gray-600"
                                      dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(result.explanation || ''),
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
      </div>
    </>
  );
};

export default QuizHistory;

